/**
 * Editor layout: top bar (workspace info + language switcher), left palette,
 * canvas. Hydrates session and workspace data on mount.
 */
import i18next from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type MeResponse, setApiMutationHook, type WorkPackageDTO } from "../api/client";
import { HelpDialog } from "../help/HelpDialog";
import { PresenceBar } from "../realtime/PresenceBar";
import {
  closeCrdtSession,
  openCrdtSession,
  setRehydrateCallback,
  subscribeCrdtStatus,
  tickAfterMutation,
} from "../realtime/session";
import { useDisplayMode } from "../state/displayMode";
import { useKeymap } from "../state/keymap";
import { isAdminOfWorkspace, useSession } from "../state/session";
import { useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";
import { AttributePanel } from "./attribute-panel/AttributePanel";
import { Canvas } from "./canvas/Canvas";
import { GlossaryCanvas } from "./canvas/GlossaryCanvas";
import { GroupEnclosePromptModal } from "./canvas/GroupEnclosePromptModal";
import { NestingPromptModal } from "./canvas/NestingPromptModal";
import { RibbonSlot } from "./canvas/RibbonSlot";
import { DevViewStateWarningsPanel } from "./DevViewStateWarningsPanel";
import { DiagramsPanel } from "./diagrams/DiagramsPanel";
import { DiagramTabs } from "./diagrams/DiagramTabs";
import { RoadmapMatrixSetupModal } from "./diagrams/RoadmapMatrixSetupModal";
import { ConceptExplorer } from "./explorer/ConceptExplorer";
import { Explorer } from "./explorer/Explorer";
import { FoldertreePanel } from "./foldertree/FoldertreePanel";
import { GridPane } from "./grid-pane/GridPane";
import { HomonymLinkPromptModal } from "./HomonymLinkPromptModal";
import { isCloseActiveTabShortcut, isEditableTarget } from "./keyboardGuards";
import { KladblokCanvas } from "./kladblok/KladblokCanvas";
import { OperationBanner } from "./OperationBanner";
import { PanelLauncher } from "./PanelLauncher";
import { ParentDeletePromptModal } from "./ParentDeletePromptModal";
import { TypePalette } from "./palette/TypePalette";
import { RepositoryMenu } from "./RepositoryMenu";
import { RelationsPanel } from "./relations/RelationsPanel";
import { ScopePicker } from "./ScopePicker";
import { SettingsMenu } from "./SettingsMenu";
import { TypeExplorer } from "./TypeExplorer";
import { TextPane } from "./text-pane/TextPane";

export function Editor() {
  const { t, i18n } = useTranslation();

  // Individual selectors so we don't subscribe to the whole session object.
  const me = useSession((s) => s.me);
  const workspaceId = useSession((s) => s.workspaceId);
  const workpackageId = useSession((s) => s.workpackageId);
  const branchId = useSession((s) => s.branchId);
  const loading = useSession((s) => s.loading);
  const error = useSession((s) => s.error);
  const exitSuperAdminOverride = useSession((s) => s.exitSuperAdminOverride);
  const displayMode = useDisplayMode((s) => s.mode);
  const setDisplayMode = useDisplayMode((s) => s.setMode);
  // Read all hooks before any early-return — Hooks must be called in the
  // same order on every render.
  const textPaneOpen = useUi((s) => s.textPaneOpen);
  const glossaryFocused = useUi((s) => s.glossaryFocused);
  const revealPaneVariant = useUi((s) => s.revealPaneVariant);
  const setRevealPaneVariant = useUi((s) => s.setRevealPaneVariant);

  // M6.5-W3b — actieve workpackage ophalen voor de Base-layout-switch.
  // Base krijgt de Foldertree-paneel in plaats van Explorer/Canvas. Voor
  // andere WPs blijft de bestaande layout. Lazy fetch — geen sessie-store
  // refactor (Explorer cachet z'n eigen lijst nog steeds).
  const [activeWorkpackage, setActiveWorkpackage] = useState<WorkPackageDTO | null>(null);
  // Refresh-counter zodat WorkpackagesSettings-CRUD via het
  // `ae:workpackages-changed`-event ook de Base-layout-switch
  // bijwerkt (bv. wanneer de huidige WP wordt verwijderd).
  const [wpRefreshCounter, setWpRefreshCounter] = useState(0);
  useEffect(() => {
    const onChanged = () => setWpRefreshCounter((c) => c + 1);
    window.addEventListener("ae:workpackages-changed", onChanged);
    return () => window.removeEventListener("ae:workpackages-changed", onChanged);
  }, []);
  useEffect(() => {
    if (!workspaceId || !workpackageId) {
      setActiveWorkpackage(null);
      return;
    }
    let cancelled = false;
    api
      .listWorkpackages(workspaceId)
      .then((r) => {
        if (cancelled) return;
        const wp = r.workpackages.find((w) => w.id === workpackageId);
        setActiveWorkpackage(wp ?? null);
      })
      .catch(() => {
        if (!cancelled) setActiveWorkpackage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, workpackageId, wpRefreshCounter]);
  const isBaseLayout = activeWorkpackage?.isBase ?? false;
  // Een lid met alleen leesrecht kijkt, en exporteert dus ook niet (besloten
  // 2026-10-17; de worker dwingt het af in `middleware/workpackageWrite.ts`).
  // De menu's tonen hem alleen wat kijken is; zie RepositoryMenu en SettingsMenu.
  const canWrite = isAdminOfWorkspace(me, workspaceId) || activeWorkpackage?.myAccess === "write";
  // Alleen kijken: pas zodra het workpackage bekend is, anders zou een
  // schrijver tijdens het laden even niets kunnen. De stores lezen het via
  // `useSession` en schrijven dan niets, ook niet vanzelf.
  const readOnly = activeWorkpackage !== null && !canWrite;
  useEffect(() => {
    useSession.getState().setReadOnly(readOnly);
  }, [readOnly]);

  // Geen browsermenu bij rechtsklikken: waar de app een eigen menu heeft, toont
  // die dat; waar niet, gebeurt er niets. Uitzonderingen: tekstvelden en het
  // Onderwater venster (CodeMirror), waar je het menu van de browser nodig
  // hebt om te kopiëren en te plakken. Geselecteerde tekst is bewust géén
  // uitzondering: op een Mac selecteert een rechtsklik zelf het woord eronder,
  // en dan zou het browsermenu toch weer verschijnen (Cmd+C werkt gewoon).
  useEffect(() => {
    const onContextMenu = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (
        el?.closest(
          "input, textarea, select, [contenteditable=''], [contenteditable='true'], .cm-editor",
        )
      ) {
        return;
      }
      e.preventDefault();
    };
    document.addEventListener("contextmenu", onContextMenu);
    return () => document.removeEventListener("contextmenu", onContextMenu);
  }, []);

  // 1. Hydrate session on mount.
  useEffect(() => {
    void useSession.getState().hydrate();
  }, []);

  // Help-dialog state — getoggled via de "?"-knop in de header én via
  // de Cmd/Ctrl+? shortcut hieronder. Hier vroeg gedeclareerd zodat de
  // shortcut-useEffect de setter kan gebruiken.
  const [helpOpen, setHelpOpen] = useState(false);

  // Globale shortcuts (M6.5 accessibility quick wins):
  //   F10           → /settings (Cmd+, valt af — Chrome onderschept 'm
  //                   voor z'n eigen instellingen voordat de pagina 'm
  //                   ziet, ondanks preventDefault)
  //   Cmd/Ctrl+/    → toggle Help-dialog (Cmd+? = Cmd+Shift+/ valt af —
  //                   shift-modifier conflict bij sommige layouts)
  //   F2            → rename geselecteerd concept of, bij geen selectie,
  //                   het actieve diagram-tab. Werkt via een custom-event
  //                   dat ConceptNode + DiagramTabs lokaal afvangen om hun
  //                   bestaande rename-state te activeren — geen nieuwe
  //                   globale state nodig.
  // Skip wanneer focus in een input/textarea/contenteditable staat, zodat
  // typen in attribuut-velden of de Vraag-Claude-prompt niet onderbroken
  // wordt door bv. F2. Capture-fase zodat React Flow / nested onKeyDowns
  // ons niet kunnen pre-empten.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // F10 — opent het nieuwe Settings-dropdown-menu via een custom-
      // event waar de SettingsMenu-component op luistert. Werkt buiten
      // editable targets én erbinnen (OS-conventie voor function-keys
      // is dat ze niet door inputs gevangen worden). Op Mac met
      // default-prefs: fn+F10 levert key="F10".
      // De oude `/settings`-route blijft tijdens de migratie bestaan
      // en is bereikbaar via de "Settings (legacy)"-link in de header.
      if (event.key === "F10") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("ae:open-settings-menu"));
        return;
      }
      const meta = event.metaKey || event.ctrlKey;
      // Cmd/Ctrl+/ — help-dialog togglen.
      if (meta && event.key === "/") {
        event.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }
      // Cmd/Ctrl+J — Onderwater venster togglen (VS Code / Chrome
      // DevTools-conventie voor "toggle bottom panel"). Werkt vanaf
      // élke focus inclusief editable targets — eindgebruiker
      // verwacht dat dit altijd opent/sluit, ongeacht of 'ie net in
      // een veld typte.
      if (meta && (event.key === "j" || event.key === "J")) {
        event.preventDefault();
        const ui = useUi.getState();
        ui.setTextPaneOpen(!ui.textPaneOpen);
        return;
      }
      // F2 — alleen buiten editable targets; anders zou rename starten
      // tijdens typen in een attribuut-veld. Voorrang: relation-label
      // > concept-naam > diagram-tab. Een geselecteerde relation wint
      // omdat de gebruiker daar net iets met geselecteerd heeft.
      if (
        event.key === "F2" &&
        !isEditableTarget(event.target) &&
        !useSession.getState().readOnly
      ) {
        const ui = useUi.getState();
        const ws = useWorkspaceData.getState();
        const relationId = ui.selectedRelationId;
        const conceptId = ui.selectedConceptId;
        const diagramId = ui.selectedDiagramId ?? ws.activeDiagramId;
        if (relationId) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("ae:rename-relation", { detail: { relationId } }));
        } else if (conceptId) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("ae:rename-concept", { detail: { conceptId } }));
        } else if (diagramId) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("ae:rename-diagram", { detail: { diagramId } }));
        }
        return;
      }
      // F8 — Repository-dropdown openen. RepositoryMenu.tsx luistert op
      // `ae:open-repository-menu` en zet z'n eigen `open`-state.
      // Werkt buiten editable targets én erbinnen (analoog aan F10).
      if (event.key === "F8") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("ae:open-repository-menu"));
        return;
      }
      // F9 — Vraag Claude-modal openen. AskClaudeButton in DiagramTabs
      // houdt de open-state lokaal, dus we triggeren via custom-event
      // (zelfde patroon als F2).
      if (event.key === "F9" && !isEditableTarget(event.target)) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("ae:open-ask-claude"));
        return;
      }
      // Alt+W — sluit het ACTIEVE diagram-tab. Cmd+W en Ctrl+W vallen
      // af: beide worden door Chrome op browser-niveau onderschept om
      // het browser-tabblad te sluiten (preventDefault werkt daar niet).
      // Alt+W is vrij; we matchen op `event.code === "KeyW"` zodat Mac's
      // Option-modifier (die "∑" als event.key levert) ook werkt. Niet in
      // een tekstveld: daar typt Option+W een teken.
      if (isCloseActiveTabShortcut(event)) {
        if (isEditableTarget(event.target)) return;
        const ws = useWorkspaceData.getState();
        const sess = useSession.getState();
        if (sess.workspaceId && ws.activeDiagramId) {
          event.preventDefault();
          event.stopPropagation();
          ws.closeDiagramTab(sess.workspaceId, ws.activeDiagramId);
        }
        return;
      }
      // Cmd/Ctrl+Shift+L — geselecteerde relatie rechttrekken. De
      // orthogonalize-logica woont in CanvasToolbar; custom-event
      // triggert 'm daar zonder code-duplicaat.
      if (meta && event.shiftKey && event.key.toLowerCase() === "l") {
        const ui = useUi.getState();
        if (ui.selectedRelationId && !useSession.getState().readOnly) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("ae:straighten-relation"));
        }
        return;
      }
      // Cmd/Ctrl+Shift+F — generieke "Fix" voor het actieve diagram.
      // Nu alleen auto-detect-nesting; later uitgebreid met orphan-
      // viewState-cleanup en andere fixes. Geen snapshot vooraf (= snelle
      // shortcut-actie); de gebruiker kan altijd Cmd+Z draaien als
      // nodig.
      if (meta && event.shiftKey && event.key.toLowerCase() === "f") {
        event.preventDefault();
        const ws = useWorkspaceData.getState();
        const sess = useSession.getState();
        if (!sess.workspaceId || !sess.branchId || !ws.activeDiagramId || sess.readOnly) return;
        const result = ws.autoDetectNestingOnDiagram(sess.workspaceId, sess.branchId, "active");
        const msg =
          result.nestedCount > 0
            ? i18next.t("app.editor.fixNestedDone", { n: result.nestedCount })
            : i18next.t("app.editor.fixNestedNone");
        useWorkspaceData.setState({ lastError: msg });
        return;
      }
      // F7 — scope-picker popup voor focus-model. Toggle: tweede F7
      // sluit 'm.
      if (event.key === "F7") {
        event.preventDefault();
        const km = useKeymap.getState();
        if (km.scopePickerOpen) km.closeScopePicker();
        else km.openScopePicker();
        return;
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  // ae:toggle-help — custom event dat de CanvasToolbar dispatcht wanneer
  // de "?"-knop wordt geklikt. Zelfde toggle-gedrag als Cmd/Ctrl+/.
  useEffect(() => {
    const onToggle = () => setHelpOpen((v) => !v);
    window.addEventListener("ae:toggle-help", onToggle);
    return () => window.removeEventListener("ae:toggle-help", onToggle);
  }, []);

  // Scope-visueel — zet `data-scope-active="true"` op het DOM-element
  // dat hoort bij de huidige useKeymap.scope, en strip 't van anderen.
  // De CSS-rule in globals.css geeft dat element een subtiele blauwe
  // ring zodat de gebruiker in een oogopslag ziet welk pane Tab/Pijl-
  // toetsen vangt (visueel equivalent van de scope-pill in de header).
  //
  // Re-fire deps: élke pane-state die bepaalt welke `[data-scope]`-
  // wrapper in de DOM zit. Wanneer TextPane↔GridPane wisselt (zelfde
  // data-scope, andere component) re-mount React de root — zonder
  // re-fire bleef de blauwe ring achter op het oude (verdwenen)
  // element. Idem bij panel-toggles waardoor data-scope-elementen in
  // of uit de DOM-tree komen.
  const activeScope = useKeymap((s) => s.scope);
  const attributePanelOpenForScope = useUi((s) => s.attributePanelOpen);
  const explorerOpenForScope = useUi((s) => s.explorerOpen);
  const typePaletteOpenForScope = useUi((s) => s.typePaletteOpen);
  const diagramsPanelOpenForScope = useUi((s) => s.diagramsPanelOpen);
  const relationsPanelOpenForScope = useUi((s) => s.relationsPanelOpen);
  useEffect(() => {
    const allScopeEls = document.querySelectorAll<HTMLElement>("[data-scope]");
    for (const el of allScopeEls) {
      if (activeScope && el.dataset.scope === activeScope) {
        el.dataset.scopeActive = "true";
      } else {
        delete el.dataset.scopeActive;
      }
    }
  }, [
    activeScope,
    revealPaneVariant,
    textPaneOpen,
    attributePanelOpenForScope,
    explorerOpenForScope,
    typePaletteOpenForScope,
    diagramsPanelOpenForScope,
    relationsPanelOpenForScope,
  ]);

  // Cmd+Z / Ctrl+Z = undo, Cmd+Shift+Z / Cmd+Y / Ctrl+Y = redo.
  //
  // Staat de cursor in een tekstveld, dan is de toets van dat veld. Anders
  // draait de model-undo. Die grens klonk hier al in de oude toelichting,
  // maar de code trok 'm niet: buiten CodeMirror draaide de model-undo
  // altijd — óók terwijl je in het attributenpaneel, een inline hernoem-veld
  // of het zoekveld van de Explorer aan het typen was. Wat je dan zag hing
  // er alleen van af of dat veld toevallig nog een eigen undo-stapel had:
  // zo niet, dan verdween er een object; zo wel, dan verdween er een letter
  // én ongemerkt een stap modelgeschiedenis. Gerald heeft dat op 2026-08-19
  // uitgeplozen (vier objecten toevoegen, twee correcties in een veld, drie
  // objecten over).
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      // CodeMirror: de focus zit op een contenteditable binnen `.cm-editor`.
      // Blijft apart genoemd zodat het pane z'n eigen undo houdt, ook als de
      // implementatie ooit van element verandert.
      if (el.closest(".cm-editor")) return true;
      return false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const meta = event.metaKey || event.ctrlKey;
      if (!meta) return;
      const key = event.key.toLowerCase();
      if (key !== "z" && key !== "y") return;
      if (isEditableTarget(event.target)) return;
      // Alleen kijken: er is niets om ongedaan te maken.
      if (useSession.getState().readOnly) return;
      // Capture-phase listener so React Flow can't preempt us.
      event.preventDefault();
      event.stopPropagation();
      if (key === "z" && !event.shiftKey) {
        void useWorkspaceData.getState().userUndo();
      } else {
        void useWorkspaceData.getState().userRedo();
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);

  // ── Scope-bound Tab-trap (commit 1 — niet-canvas-scopes) ──
  //
  // Wanneer er een scope actief is (via F7 of muis-klik), houdt Tab /
  // Shift+Tab focus BINNEN het scope-paneel. Cyclet door alle tabable
  // DOM-elementen onder `[data-scope="<scope>"]`. Browser-default Tab
  // (die naar de volgende tabable in de hele page-tree gaat, vaak buiten
  // de scope) wordt onderschept met `preventDefault`.
  //
  // Canvas-scope krijgt een eigen Tab-handler in commit 2 (cyclet via
  // buildFocusables door canvas-objecten i.p.v. DOM-tabables) — daarom
  // skipt deze handler scope=canvas.
  //
  // Edge-cases / mitigaties (zie `feedback_keyboard_binding_caution.md`
  // — gericht impact-map vóór implementatie):
  //  - **Editable targets** (input/textarea/select/contenteditable,
  //    inclusief CodeMirror's `.cm-content`): doorlaten zodat CM's
  //    eigen Tab-keymap (indent) en browser-native field-tabbing
  //    werken.
  //  - **Modals / popups** (`[role="dialog"]` of `[aria-modal="true"]`):
  //    doorlaten zodat InsertRowDialog / StereotypeEditModal /
  //    AskClaudeModal / ScopePicker hun eigen Tab-cycle hebben.
  //  - **Geen scope actief** (scope = null): doorlaten — default browser-
  //    gedrag.
  //  - **Lege scope-pane** (0 tabables gevonden): doorlaten zodat de
  //    gebruiker niet "vastzit" in een lege pane.
  useEffect(() => {
    const isInEditableTarget = (el: HTMLElement | null): boolean => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      // CodeMirror 6: focus zit op `.cm-content` (een contenteditable),
      // bovenstaande check zou 't al pakken — maar als CM ooit z'n
      // structuur wijzigt vangt de class-check 't nog.
      if (el.closest(".cm-editor")) return true;
      return false;
    };

    const isInModalOrPopup = (el: HTMLElement | null): boolean => {
      if (!el) return false;
      return !!el.closest('[role="dialog"], [aria-modal="true"]');
    };

    const isVisible = (el: HTMLElement): boolean => {
      // offsetParent is null voor display:none + voor fixed/absolute
      // elementen die buiten een positioned parent vallen. Voor onze
      // panes is dit een goede proxy: niet-gemounte panes hebben null.
      return el.offsetParent !== null || el.getClientRects().length > 0;
    };

    const collectTabables = (root: HTMLElement): HTMLElement[] => {
      const sel =
        'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), ' +
        "select:not([disabled]), textarea:not([disabled]), summary, " +
        '[tabindex]:not([tabindex="-1"])';
      const list = Array.from(root.querySelectorAll<HTMLElement>(sel));
      return list.filter((el) => {
        // Standaard visibility-check (display:none / off-screen).
        if (!isVisible(el)) return false;
        // Defensief: aria-hidden-elementen skippen — die zijn voor
        // assistive tech onbereikbaar, dus Tab hoort ze ook niet te raken.
        if (el.closest('[aria-hidden="true"]')) return false;
        // Native <details>-collapsed: browsers tonen content-area niet,
        // maar de DOM-children blijven `display: revert` zodat
        // querySelectorAll ze terugvindt. Filter expliciet: in een
        // gesloten <details> is alleen z'n eigen <summary> tabable
        // (de toggle-knop), niets binnen de content-area.
        const closedDetails = el.closest("details:not([open])");
        if (closedDetails) {
          return el.tagName === "SUMMARY" && el.parentElement === closedDetails;
        }
        return true;
      });
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const km = useKeymap.getState();
      if (km.scope === null) return; // geen scope → browser-default
      if (km.scope === "canvas") return; // commit 2 verzorgt canvas
      const target = e.target as HTMLElement | null;
      if (isInEditableTarget(target)) return;
      if (isInModalOrPopup(target)) return;
      const scopeEl = document.querySelector<HTMLElement>(`[data-scope="${km.scope}"]`);
      if (!scopeEl) return;
      const tabables = collectTabables(scopeEl);
      if (tabables.length === 0) return;
      // Vind huidige focus-index. Als focus buiten scope (bv. nét uit
      // ScopePicker), start de cycle op index 0 (eerste tabable).
      const active = document.activeElement as HTMLElement | null;
      let idx = active ? tabables.indexOf(active) : -1;
      if (idx === -1) {
        idx = e.shiftKey ? tabables.length - 1 : 0;
      } else {
        idx = e.shiftKey ? idx - 1 : idx + 1;
        // Wrap rond — Tab vanaf laatste → eerste; Shift+Tab vanaf
        // eerste → laatste.
        if (idx < 0) idx = tabables.length - 1;
        if (idx >= tabables.length) idx = 0;
      }
      const next = tabables[idx];
      if (!next) return;
      e.preventDefault();
      e.stopPropagation();
      next.focus();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  // ── Alt/Option+Tab toggle tussen Canvas en Onderwater venster ──
  //
  // Wisselt `useKeymap.scope` tussen `"canvas"` en `"text-pane"`. Bij
  // doel = text-pane: opent het Onderwater venster als 't dicht is +
  // verplaatst browser-focus naar de eerste tabable daar (zelfde
  // patroon als ScopePicker.commitChoice). Bij doel = canvas: alleen
  // scope-set — geen .focus()-shift op canvas-wrapper (bewuste keuze
  // na de Option+Tab-fout van 2026-05-23 die RF's keyboard-flow brak).
  //
  // Vanuit elke andere scope (Explorer, AttributePanel, etc.) is de
  // default Alt+Tab → canvas. Tweede Alt+Tab brengt dan naar text-pane.
  //
  // Mitigaties (uit `feedback_keyboard_binding_caution.md` impact-map):
  //  - Editable targets (CodeMirror, input/textarea/etc.) doorlaten —
  //    CM's eigen Esc-then-Tab-flow blijft werken
  //  - Modals/popups doorlaten zodat InsertRowDialog / StereotypeEdit /
  //    ScopePicker hun eigen Tab-flow houden
  //  - Geen conflict met commit 1 / commit 2 Tab-handlers: die filteren
  //    Alt-modifier expliciet uit
  //
  // Zowel TextPane als GridPane dragen `data-scope="text-pane"` op hun
  // root, dus focus-shift, blauwe scope-ring en muis-klik-scope-detect
  // werken in beide Onderwater-varianten.
  useEffect(() => {
    const isInEditableTarget = (el: HTMLElement | null): boolean => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      if (el.closest(".cm-editor")) return true;
      return false;
    };
    const isInModalOrPopup = (el: HTMLElement | null): boolean => {
      if (!el) return false;
      return !!el.closest('[role="dialog"], [aria-modal="true"]');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Tab") return;
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      if (isInEditableTarget(target)) return;
      if (isInModalOrPopup(target)) return;
      e.preventDefault();
      e.stopPropagation();
      const km = useKeymap.getState();
      const next: "canvas" | "text-pane" = km.scope === "canvas" ? "text-pane" : "canvas";
      if (next === "text-pane") {
        const ui = useUi.getState();
        if (!ui.textPaneOpen) ui.setTextPaneOpen(true);
      }
      km.setScope(next);
      if (next === "text-pane") {
        setTimeout(() => {
          const scopeEl = document.querySelector<HTMLElement>('[data-scope="text-pane"]');
          const first = scopeEl?.querySelector<HTMLElement>(
            'button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
          );
          first?.focus();
        }, 0);
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  // 2. After session is loaded, hydrate workspace data and ensure a default diagram.
  useEffect(() => {
    if (!workspaceId || !branchId) {
      return;
    }
    const wsData = useWorkspaceData.getState();
    void wsData.hydrate(workspaceId, branchId).then(() => {
      const refreshed = useWorkspaceData.getState();
      // Een lezer maakt geen diagram aan, ook niet vanzelf.
      if (!refreshed.activeDiagramId && !useSession.getState().readOnly) {
        void refreshed.ensureDefaultDiagram(workspaceId, branchId);
      }
    });
  }, [workspaceId, branchId]);

  // 3. Open de realtime CRDT-sessie. De provider ontvangt updates van
  // peers; bij elke remote-update herhydrateren we de store. Lokale
  // mutaties gaan nog steeds via de bestaande API-flow (M3 first cut)
  // maar sturen een POKE naar peers zodat zij óók rehydraten.
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    setRehydrateCallback((wsId, brId) => {
      void useWorkspaceData.getState().hydrate(wsId, brId);
    });
    setApiMutationHook(() => {
      // tick i.p.v. poke — gaat via MSG_UPDATE-kanaal dat we
      // bewezen werkend hebben gezien via initial sync.
      tickAfterMutation();
    });
    openCrdtSession(workspaceId, branchId);
    return () => {
      closeCrdtSession();
      setRehydrateCallback(null);
      setApiMutationHook(null);
    };
  }, [workspaceId, branchId]);

  // Status-pill in de header: "live", "syncing", "offline"…
  const [crdtStatus, setCrdtStatus] = useState<
    "idle" | "connecting" | "connected" | "syncing" | "offline"
  >("idle");
  useEffect(() => {
    const unsub = subscribeCrdtStatus(setCrdtStatus);
    return unsub;
  }, []);

  // Tab / Shift+Tab / Arrow Up/Down — cycle door focusable DOM-elementen
  // binnen huidige scope. Canvas-scope handelt z'n eigen Tab + Arrow
  // (cursor-cel). Geen scope = native browser-Tab. Voor inputs / textareas
  // / selects laten we Arrow Up/Down met rust zodat de browser z'n
  // native cursor-beweging of option-switch doet.
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onPanelNav = (e: KeyboardEvent) => {
      // Native browser-Tab handelt Tab in panelen — die skipt automatisch
      // hidden items binnen closed `<details>` en springt naar de
      // volgende focusable. Onze handler alleen voor Arrow Up/Down (= een
      // alternatieve manier om door dezelfde focusables te cyclen
      // zonder de hele toetsenbord-flow te kapen).
      const isArrowDown = e.key === "ArrowDown";
      const isArrowUp = e.key === "ArrowUp";
      if (!isArrowDown && !isArrowUp) return;
      // Detecteer scope via de DOM-pad omhoog vanaf het actieve element.
      const activeEl = document.activeElement as HTMLElement | null;
      const scopeEl = activeEl?.closest<HTMLElement>("[data-scope]");
      if (!scopeEl) return;
      const scope = scopeEl.dataset.scope;
      if (!scope || scope === "canvas") return;
      // Skip als focus in een editable veld zit — anders verstoor je
      // native cursor-beweging in textareas/inputs en option-switching
      // in <select>.
      if (inEditableField(e.target)) return;
      const tabbables = Array.from(
        scopeEl.querySelectorAll<HTMLElement>(
          'button, [href], input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.offsetParent !== null &&
          getComputedStyle(el).visibility !== "hidden",
      );
      if (tabbables.length === 0) return;
      e.preventDefault();
      const activeIdx = tabbables.indexOf(document.activeElement as HTMLElement);
      const isPrev = isArrowUp;
      let nextIdx: number;
      if (activeIdx === -1) {
        nextIdx = isPrev ? tabbables.length - 1 : 0;
      } else {
        nextIdx = activeIdx + (isPrev ? -1 : 1);
        if (nextIdx < 0) nextIdx = tabbables.length - 1;
        if (nextIdx >= tabbables.length) nextIdx = 0;
      }
      const target = tabbables[nextIdx];
      if (target) {
        target.focus();
        // Houd target in beeld bij lange lijsten (Explorer-tree).
        target.scrollIntoView({ block: "nearest", behavior: "auto" });
      }
    };
    window.addEventListener("keydown", onPanelNav, { capture: true });
    return () => window.removeEventListener("keydown", onPanelNav, { capture: true });
  }, []);

  // Niet-sticky muis-scope-switch: bij elke pointerdown bepaalt onze
  // listener welke scope geraakt is op basis van het dichtstbijzijnde
  // `data-scope`-attribuut op de DOM-pad omhoog vanaf de click-target.
  // Geen onClick-wrapper per paneel; alleen een attribuut op de root.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const el = target.closest<HTMLElement>("[data-scope]");
      const next = (el?.dataset.scope ?? null) as ReturnType<typeof useKeymap.getState>["scope"];
      if (!next) return;
      const km = useKeymap.getState();
      if (km.scope !== next) km.setScope(next);
      // Muis = geen grid-cel. Bij elke muis-klik in canvas / toolbar
      // wissen we de cursor + focus-ring zodat de visuele grid-state
      // alleen verschijnt na een toetsenbord-actie.
      if (next === "canvas" || next === "toolbar") {
        km.setCanvasCursor(null);
        km.setCanvasFocus(null);
      }
    };
    window.addEventListener("pointerdown", onMouseDown, { capture: true });
    return () => window.removeEventListener("pointerdown", onMouseDown, { capture: true });
  }, []);

  // App-start default-scope: canvas wanneer een diagram open is, anders
  // Explorer. Bewust pas geset zodra workspace+activeDiagram klaar zijn,
  // dus na de eerste hydrate-roundtrip — bij Base-WP-toegang die geen
  // diagrammen heeft valt 't terug op Explorer.
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const initialScopeSet = useRef(false);
  useEffect(() => {
    if (initialScopeSet.current) return;
    if (!workspaceId || !branchId) return;
    initialScopeSet.current = true;
    useKeymap.getState().setScope(activeDiagramId ? "canvas" : "explorer");
  }, [workspaceId, branchId, activeDiagramId]);

  if (error) {
    return (
      <main className="flex h-full items-center justify-center bg-slate-100 p-8">
        <div className="max-w-md rounded border border-rose-300 bg-white p-6 shadow">
          <h1 className="font-semibold text-rose-900 text-xl">{t("app.status.error")}</h1>
          <p className="mt-2 text-slate-700 text-sm">{error}</p>
        </div>
      </main>
    );
  }

  if (loading || !me) {
    return (
      <main className="flex h-full items-center justify-center bg-slate-100">
        <p className="text-slate-500">{t("app.status.loading")}</p>
      </main>
    );
  }

  // Super admin zonder eigen workspace-membership (puur helper-account) —
  // GEEN foutstate; toon een minimale header zodat Settings → Wissel
  // workspace bereikbaar is om er eentje te kiezen.
  if (!workspaceId && me.is_super_admin) {
    return (
      <div className="flex h-full flex-col">
        <header className="ledger flex items-center justify-between border-(--ld-border) border-b bg-(--ld-surface) px-4 py-2">
          <h1 className="font-semibold text-(--ld-text)">{t("app.title")}</h1>
          <div className="flex items-center gap-3 text-(--ld-muted) text-xs">
            <span className="font-mono">{me.email}</span>
            <SettingsMenu />
          </div>
        </header>
        <main className="flex flex-1 items-center justify-center bg-slate-100">
          <p className="max-w-md text-center text-slate-500 text-sm">
            {t("app.status.superAdminNoWorkspace")}
          </p>
        </main>
      </div>
    );
  }

  const ws = me.workspaces.find((w) => w.workspace_id === workspaceId);
  const overrideActive = me.super_admin_override?.workspace_id === workspaceId;
  const displayWorkspaceName = ws?.workspace_name ?? me.super_admin_override?.workspace_name;

  return (
    <div className="flex h-full flex-col">
      <header
        data-keep-attribute-panel-open
        className="ledger flex items-center justify-between gap-3 border-(--ld-border) border-b bg-(--ld-surface) px-4 py-2"
      >
        {/* Vanaf iPad-breedte (768px) past alles op één regel: links mag de
            workspacenaam inkorten, rechts vouwen labels in tot hun stip of
            teken (de tekst blijft voor schermlezers en in de tooltip). */}
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="whitespace-nowrap font-semibold text-(--ld-text)">{t("app.title")}</h1>
          <WorkspaceTitleSelector
            currentWorkspaceId={workspaceId}
            displayName={displayWorkspaceName}
            memberships={me.workspaces}
            overrideActive={overrideActive}
          />
          {overrideActive && (
            <span className="flex min-w-0 items-center gap-1.5 rounded bg-(--ld-notice-bg) px-2 py-0.5 text-(--ld-notice-ink) text-xs">
              <span className="truncate">
                {t("settings.superAdmin.overrideBanner", { name: displayWorkspaceName })}
              </span>
              <button
                type="button"
                onClick={() => void exitSuperAdminOverride()}
                className="shrink-0 whitespace-nowrap underline hover:text-(--ld-notice-ink)"
              >
                {t("settings.superAdmin.overrideBackButton")}
              </button>
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-(--ld-muted) text-xs lg:gap-3">
          <PresenceBar />
          <CrdtStatusPill status={crdtStatus} />
          <ScopeIndicator />
          {/* Onder 1280px staat het adres in de tooltip van Uitloggen. */}
          <span className="hidden font-mono xl:inline">{me.email}</span>
          <div className="inline-flex overflow-hidden rounded border border-(--ld-control)">
            <SegmentedToggle
              label="NL"
              active={i18n.language === "nl"}
              onClick={() => {
                void i18n.changeLanguage("nl");
                // Persist server-side; ignore failures so a flaky network
                // doesn't block the visual switch.
                void api.patchMe({ ui_language: "nl" }).catch(() => {});
              }}
            />
            <SegmentedToggle
              label="EN"
              active={i18n.language === "en"}
              onClick={() => {
                void i18n.changeLanguage("en");
                void api.patchMe({ ui_language: "en" }).catch(() => {});
              }}
            />
          </div>
          {/* ArchiMate-weergavemodus — puur presentatie, geen modelwijziging.
              Zie state/displayMode.ts en archimate4-migratie.md fase 3. */}
          <div className="inline-flex overflow-hidden rounded border border-(--ld-control)">
            <SegmentedToggle
              label={t("app.header.displayModeV31")}
              title={t("app.header.displayModeV31Title")}
              active={displayMode === "v31"}
              onClick={() => setDisplayMode("v31")}
            />
            <SegmentedToggle
              label={t("app.header.displayModeV4")}
              title={t("app.header.displayModeV4Title")}
              active={displayMode === "v4"}
              onClick={() => setDisplayMode("v4")}
            />
          </div>
          {readOnly && (
            <span
              className="rounded border border-transparent bg-(--ld-notice-bg) px-2 py-0.5 font-semibold text-(--ld-notice-ink)"
              title={t("app.header.readOnlyTitle")}
            >
              {t("app.header.readOnly")}
            </span>
          )}
          {/* Voor een lezer toont het menu alleen wat kijken is (glossary,
              type explorer, workpackage-instellingen alleen-lezen). */}
          <RepositoryMenu workspaceId={workspaceId} branchId={branchId} />
          {/* "?"-help-knop verhuisd naar Canvas-toolbar (zodat scope=toolbar
              altijd minstens één tabable heeft voor F7→8). Cmd/Ctrl+/
              shortcut blijft via de globale handler hieronder werken. */}
          {/* Een lezer houdt Account en API-tokens; zie SettingsMenu. */}
          <SettingsMenu />
          <button
            type="button"
            className="whitespace-nowrap rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-semibold text-(--ld-text) hover:bg-(--ld-raised)"
            title={t("app.header.logoutAs", { email: me.email })}
            onClick={() => {
              void api.logout().finally(() => {
                window.location.assign("/login");
              });
            }}
          >
            <span aria-hidden="true">⎋</span>{" "}
            <span className="sr-only lg:not-sr-only">{t("app.header.logoutTitle")}</span>
          </button>
        </div>
      </header>
      {/* De aftelling van een aangekondigde import of restore. Boven het werk
          en niet in een hoek: wie aan iets nieuws begint hoort de resterende
          tijd te zien op het moment dat 'ie beslist. */}
      <OperationBanner />
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <TypeExplorer />
      <ScopePicker />
      <DevViewStateWarningsPanel />
      <div className="relative flex h-full min-h-0 flex-row overflow-x-auto">
        <PanelLauncher />
        {!readOnly && <TypePalette unconstrained={isBaseLayout} />}
        {isBaseLayout ? (
          <>
            <FoldertreePanel />
            <main className="relative flex min-w-[480px] flex-1 flex-col bg-slate-50">
              <div className="relative min-h-0 flex-1">
                <KladblokCanvas />
                <AttributePanel />
              </div>
            </main>
          </>
        ) : (
          <>
            <Explorer />
            <ConceptExplorer />
            <RelationsPanel />
            <DiagramsPanel />
            <main className="relative flex min-w-[480px] flex-1 flex-col bg-slate-50">
              {/* De werkbalk van het canvas: een vaste balk bovenaan, tussen de
                  linkerpanelen en de rechterrand (`canvas/RibbonSlot.tsx`). */}
              <RibbonSlot />
              <DiagramTabs />
              <div className="relative min-h-0 flex-1">
                {/*
                  key={branch:diagram}: forceer een schone REMOUNT van de canvas
                  bij élke view-wissel (branch ÉN diagram-tab). Anders blijft
                  React-Flow's interne state (edge-paden, handle-bounds, node-
                  metingen) van de vorige view hangen na de data-swap → optisch
                  corrupte edges tot een hard refresh (#47). Remount = zelfde
                  effect als hard refresh, zonder de pagina te herladen; de
                  hide-tot-fit in Canvas dekt de flikker.
                */}
                {glossaryFocused ? (
                  // Virtuele Glossary-view (Sharp Attribuuttypes) — vervangt de
                  // gewone Canvas; geopend via de Explorer-node "Attribuuttypes".
                  <GlossaryCanvas />
                ) : (
                  <Canvas key={`${branchId ?? "no-branch"}:${activeDiagramId ?? "none"}`} />
                )}
                {/*
                  AttributePanel woont binnen het canvas-inner div zodat z'n
                  absolute-positioning (left-2 / right-2) zich beperkt tot
                  de canvas-rechthoek. Daarbuiten plaatsen zou 'm aan de
                  ver-linkerkant over Explorer / TypePalette / etc. heen
                  laten landen wanneer 'ie naar left flipt.
                */}
                <AttributePanel />
              </div>
              {textPaneOpen ? (
                <div className="ledger flex min-h-0 basis-2/5 flex-col border-(--ld-control) border-t bg-(--ld-surface)">
                  {/* Dev-toggle: tabel-prototype naast bestaande tekst-editor.
                      Verdwijnt zodra GridPane volwaardig de TextPane vervangt. */}
                  <div className="flex shrink-0 items-center gap-1 border-(--ld-border) border-b bg-(--ld-raised) px-2 py-1 text-[11px]">
                    <span className="text-(--ld-muted)">{t("textPane.variantSelectorLabel")}</span>
                    <button
                      type="button"
                      className={`rounded px-2 py-0.5 ${
                        revealPaneVariant === "text"
                          ? "bg-(--ld-primary) text-(--ld-primary-ink)"
                          : "text-(--ld-muted) hover:bg-(--ld-active) hover:text-(--ld-text)"
                      }`}
                      onClick={() => setRevealPaneVariant("text")}
                    >
                      {t("textPane.variantTextLegacy")}
                    </button>
                    <button
                      type="button"
                      className={`rounded px-2 py-0.5 ${
                        revealPaneVariant === "grid"
                          ? "bg-(--ld-primary) text-(--ld-primary-ink)"
                          : "text-(--ld-muted) hover:bg-(--ld-active) hover:text-(--ld-text)"
                      }`}
                      onClick={() => setRevealPaneVariant("grid")}
                    >
                      {t("textPane.variantGrid")}
                    </button>
                  </div>
                  <div className="min-h-0 flex-1">
                    {revealPaneVariant === "grid" ? <GridPane /> : <TextPane />}
                  </div>
                </div>
              ) : (
                <div className="ledger flex justify-end border-(--ld-control) border-t bg-(--ld-raised) px-2 py-1">
                  <TextPane />
                </div>
              )}
            </main>
          </>
        )}
      </div>
      <NestingPromptModal />
      <GroupEnclosePromptModal />
      <ParentDeletePromptModal />
      <HomonymLinkPromptModal />
      <RoadmapMatrixSetupModal />
    </div>
  );
}

/** Naam van de actieve workspace naast de app-titel. Ziet er hetzelfde uit
 *  als de titel; is er meer dan één workspace, dan opent 'ie een keuze-lijst
 *  om te wisselen (via `session.switchWorkspace`). Een actieve super-admin-
 *  override blijft plain tekst — wisselen gaat daar via Settings. */
function WorkspaceTitleSelector({
  currentWorkspaceId,
  displayName,
  memberships,
  overrideActive,
}: {
  currentWorkspaceId: string | null;
  displayName: string | undefined;
  memberships: MeResponse["workspaces"];
  overrideActive: boolean;
}) {
  const { t } = useTranslation();
  const switchWorkspace = useSession((s) => s.switchWorkspace);
  if (!displayName) return null;
  const canSwitch = !overrideActive && memberships.length > 1;
  // Een lange naam kort in met "…"; de volledige naam staat in de tooltip.
  const nameClass = "block max-w-[11rem] truncate font-semibold text-(--ld-text) xl:max-w-[20rem]";
  if (!canSwitch) {
    return (
      <span className={nameClass} title={displayName}>
        {displayName}
      </span>
    );
  }
  return (
    <label className="relative inline-flex min-w-0 items-center gap-1" title={displayName}>
      <span className={nameClass}>{displayName}</span>
      {/* Getekende pijl, even groot als die van een keuzelijst; "▾" bleef klein. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 16 16"
        className="h-4 w-4 shrink-0 text-(--ld-text)"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m4 6 4 4 4-4" />
      </svg>
      <select
        aria-label={t("app.header.chooseWorkspace")}
        value={currentWorkspaceId ?? ""}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== currentWorkspaceId) void switchWorkspace(next);
        }}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0"
      >
        {memberships.map((w) => (
          <option key={w.workspace_id} value={w.workspace_id}>
            {w.workspace_name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Toont de actieve focus-scope in de header. F7 opent de scope-picker;
 *  klikken op het pill doet hetzelfde voor wie 't shortcut niet weet. */
function ScopeIndicator() {
  const scope = useKeymap((s) => s.scope);
  const openPicker = useKeymap((s) => s.openScopePicker);
  const { t } = useTranslation();
  if (!scope) return null;
  // We hergebruiken de SCOPE_LABELS-mapping uit keymap.ts — single
  // source of truth voor de NL/EN-namen. Niet via import om circulariteit
  // te vermijden; in plaats daarvan een mini-mapping hier.
  const labelMap: Record<NonNullable<typeof scope>, string> = {
    canvas: t("scopePicker.short.canvas"),
    explorer: t("scopePicker.short.explorer"),
    "diagrams-panel": t("scopePicker.short.diagramsPanel"),
    "type-palette": t("scopePicker.short.typePalette"),
    "relations-panel": t("scopePicker.short.relationsPanel"),
    "text-pane": t("scopePicker.short.textPane"),
    "attribute-panel": t("scopePicker.short.attributePanel"),
    toolbar: t("scopePicker.short.toolbar"),
  };
  const label = labelMap[scope];
  return (
    <button
      type="button"
      onClick={openPicker}
      className="inline-flex items-center gap-1 rounded bg-(--ld-active) px-1.5 py-0.5 font-medium text-xs text-(--ld-accent) hover:bg-(--ld-active)"
      title={t("app.header.scopeIndicatorTitle")}
      aria-keyshortcuts="F7"
    >
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
      <span className="sr-only lg:not-sr-only">{label}</span>
    </button>
  );
}

function CrdtStatusPill({
  status,
}: {
  status: "idle" | "connecting" | "connected" | "syncing" | "offline";
}) {
  // Toon alleen iets zinvols zodra de sessie probeert te verbinden —
  // bij "idle" (provider nog niet opgestart) blijft het pill leeg
  // zodat de single-user-modus niets in de UI verandert.
  const { t } = useTranslation();
  if (status === "idle") return null;
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${config.cls}`}
      title={t(config.titleKey)}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${config.dotCls}`} />
      <span className="sr-only lg:not-sr-only">{t(config.labelKey)}</span>
    </span>
  );
}

const STATUS_CONFIG: Record<
  "connecting" | "connected" | "syncing" | "offline",
  { labelKey: string; cls: string; dotCls: string; titleKey: string }
> = {
  connecting: {
    labelKey: "app.header.crdt.connecting",
    cls: "bg-(--ld-active) text-(--ld-text)",
    dotCls: "bg-slate-400 animate-pulse",
    titleKey: "app.header.crdt.connectingTitle",
  },
  syncing: {
    labelKey: "app.header.crdt.syncing",
    cls: "bg-(--ld-notice-bg) text-(--ld-notice-ink)",
    dotCls: "bg-amber-500 animate-pulse",
    titleKey: "app.header.crdt.syncingTitle",
  },
  connected: {
    labelKey: "app.header.crdt.connected",
    cls: "bg-(--ld-positive-bg) text-(--ld-positive-ink)",
    dotCls: "bg-emerald-500",
    titleKey: "app.header.crdt.connectedTitle",
  },
  offline: {
    labelKey: "app.header.crdt.offline",
    cls: "bg-rose-100 text-(--ld-negative-ink)",
    dotCls: "bg-rose-500",
    titleKey: "app.header.crdt.offlineTitle",
  },
};

/** Eén knop uit een segmented control in de header. Gebruikt voor de
 *  taalschakelaar (NL/EN) én voor de ArchiMate-weergavemodus (3.1/4). */
function SegmentedToggle({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...(title ? { title } : {})}
      className={`px-2 py-1 font-medium text-xs ${
        active
          ? "bg-slate-700 text-white"
          : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
      }`}
    >
      {label}
    </button>
  );
}
